import { computed, type MaybeRefOrGetter } from 'vue'

import { useAuth } from '@common/auth-layer/composables/useAuth'
import { useLearnerScopedLocationId } from '@courses/admin-preview-layer/composables/useLearnerScopedLocationId'
import { type LibraryProducts } from '@courses/types-layer/types/library'

import { useLearnerApiQueryStore, type LearnerApiQueryStoreOptions } from '../stores/learner-api.query-store'

/**
 * Hard cap enforced by `/lessons/sequence/bulk`: payloads above this many courses are rejected.
 * We bound the started-course list to it so a learner with > 100 started courses still resolves
 * resume data for the first 100 instead of the backend failing the entire batch.
 */
const MAX_BULK_NEXT_INCOMPLETE_COURSES = 100

/**
 * Learner API Composable
 *
 * Provides access to learner-related queries and mutations.
 * This is a simple wrapper around the query store that follows the
 * standard composable → query store pattern.
 *
 * Returns actual TanStack Query objects (not wrapped in try-catch)
 * to preserve all query methods including suspense() for SSR.
 *
 * No singleton needed - TanStack Query handles deduplication based on query keys.
 */
export const useLearnerApi = (
  courseId: MaybeRefOrGetter<string>,
  courseLocationId: MaybeRefOrGetter<string>,
  options?: LearnerApiQueryStoreOptions
) => {
  const {
    fetchNextIncompleteLessonQuery,
    createNextIncompleteLessonForAllCoursesMutation,
    fetchUnlockedModulesQuery,
    fetchCompletedLessonsQuery,
    fetchSalesDataQuery,
  } = useLearnerApiQueryStore(courseId, useLearnerScopedLocationId(courseLocationId), options)

  // Return query factory functions directly - no try-catch wrapping
  // TanStack Query handles errors internally via error states
  const getNextInCompleteLesson = (lessonId?: MaybeRefOrGetter<string>) => {
    return fetchNextIncompleteLessonQuery(lessonId)
  }

  const getUnlockedModules = () => {
    return fetchUnlockedModulesQuery()
  }

  const getCompletedLessons = () => {
    return fetchCompletedLessonsQuery()
  }

  const getSalesData = (offerId?: MaybeRefOrGetter<string>, source?: MaybeRefOrGetter<string>) => {
    return fetchSalesDataQuery(offerId, source)
  }

  const getNextIncompleteForAllCourses = () => {
    const { mutate, data, isPending, error } = createNextIncompleteLessonForAllCoursesMutation()
    const { store: authStore } = useAuth()

    /**
     * Prefetch the next-incomplete lesson for every course the learner has a resume point for.
     * "Has progress" mirrors `getMyCoursesProgressState` (cp-courses-layer): a course counts as
     * in-progress when `courseStarted` is true OR `completedPosts > 0` — the latter covers courses
     * whose progress lives only in `completedPosts` while `courseStarted` is still false. Fully
     * unstarted courses contribute a row the navigation flow never reads (deep-link is gated on
     * progress), so we drop them and skip the call entirely when logged out. The list is then
     * bounded to {@link MAX_BULK_NEXT_INCOMPLETE_COURSES} to stay within the BE's
     * `/lessons/sequence/bulk` cap.
     */
    const prefetchForStartedCourses = (products: LibraryProducts | null | undefined) => {
      if (!products || !authStore.isLoggedIn) return
      const resumableProducts = products
        .filter(product => product.courseStarted || (product.completedPosts ?? 0) > 0)
        .slice(0, MAX_BULK_NEXT_INCOMPLETE_COURSES)
      if (resumableProducts.length > 0) {
        mutate(resumableProducts)
      }
    }

    return {
      mutate,
      prefetchForStartedCourses,
      data: computed(() => data.value || {}),
      isLoading: computed(() => isPending.value),
      error,
    }
  }

  return {
    getNextInCompleteLesson,
    getNextIncompleteForAllCourses,
    getUnlockedModules,
    getCompletedLessons,
    getSalesData,
  }
}
